import React, { memo, useCallback, useMemo, useState } from 'react'

import { Icon, MoreChorizontal, RotatingSpinner, classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, bool, number, oneOfType, shape, string } from 'prop-types'

import useDeleteImageRun from 'Pods/ThreeDStudio/Hooks/useDeleteImageRun'
import usePrivateModeAccess from 'Pods/ThreeDStudio/Hooks/usePrivateModeAccess'
import useStudioAccessTierPending from 'Pods/ThreeDStudio/Hooks/useStudioAccessTierPending'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import useUpdateRunPrivacy from 'Pods/ThreeDStudio/Hooks/useUpdateRunPrivacy'
import DeleteConfirmModal from 'Pods/ThreeDStudio/Shared/DeleteConfirmModal/DeleteConfirmModal'
import Dropdown from 'Pods/ThreeDStudio/Shared/Dropdown/Dropdown'

import ImageCardContent from './Components/ImageCardContent/ImageCardContent'

const size = { height: '36px', width: '36px' }
const spinnerSize = { height: '24px', width: '24px' }
const icon = (
  <Icon notSpaced svgComponent={MoreChorizontal} color="dark" size={size} className="model-card__icon-dropdown" />
)

// Matches ModelCard: the menu trigger wrapper sits at z-index 452, so the portal'd menu
// has to clear it for tooltips overflowing the menu.
const PORTAL_Z_INDEX = 453

// One generated image in My Assets > Images. Shares the .model-card block with ModelCard
// because Figma draws both from the same `Model preview` component; only the menu and the
// delete target differ (a run here, a whole session there).
function ImageCard({ runId, attributes, isProcessing = false }) {
  const t = useTranslation()
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [shouldCloseDropdown, setShouldCloseDropdown] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const { outputAssets, title, prompt, createdAt, private: isPrivateAttribute } = attributes || {}
  // The image providers store the generated image as the asset file and attach no preview,
  // so the file itself is the fallback thumbnail.
  const previewUrl = outputAssets?.[0]?.previewUrl ?? outputAssets?.[0]?.fileUrl
  const imageTitle = title ?? prompt ?? ''

  const handleOpenDelete = useCallback(() => {
    setIsDeleteModalOpen(true)
    setShouldCloseDropdown(true)
  }, [])

  const handleCloseDelete = useCallback(() => {
    setIsDeleteModalOpen(false)
  }, [])

  const { deleteImage, isLoading: isDeleting } = useDeleteImageRun({ onSuccess: handleCloseDelete })

  const handleConfirmDelete = useCallback(() => {
    deleteImage(runId)
  }, [deleteImage, runId])

  // Same reasoning as ModelCard: the library is client-only, so gating on the
  // login-pending window is safe, and a logged-in user is treated as entitled while the
  // tier resolves so the paid-only crown never flashes for subscribers.
  const hasPrivateModeAccess = usePrivateModeAccess()
  const isTierPending = useStudioAccessTierPending()
  const canTogglePrivacy = hasPrivateModeAccess || isTierPending
  const { updateRunPrivacy, isLoading: isPrivacyUpdating } = useUpdateRunPrivacy()
  // useUpdateRunPrivacy reflects the new value by patching the *sessions* cache, which
  // holds no row for an image run, so the toggle is tracked here instead and the server
  // value takes over on the next fetch.
  const [isPrivate, setIsPrivate] = useState(Boolean(isPrivateAttribute))

  const handleTogglePrivacy = useCallback(() => {
    setIsPrivate((wasPrivate) => !wasPrivate)
    updateRunPrivacy({ runId, isPrivate: !isPrivate })
  }, [updateRunPrivacy, runId, isPrivate])

  // runs#destroy rejects a run that is still in flight, so an in-flight card's menu stays
  // read-only until the run settles.
  const content = useMemo(() =>
    <ImageCardContent
      title={imageTitle}
      createdAt={createdAt}
      runId={runId}
      onDelete={handleOpenDelete}
      isDeleting={isDeleting}
      isPrivate={isPrivate}
      canTogglePrivacy={canTogglePrivacy}
      onTogglePrivacy={handleTogglePrivacy}
      isPrivacyUpdating={isPrivacyUpdating}
      isReadOnly={isProcessing}
    />,
    [
      imageTitle,
      createdAt,
      runId,
      handleOpenDelete,
      isDeleting,
      isPrivate,
      canTogglePrivacy,
      handleTogglePrivacy,
      isPrivacyUpdating,
      isProcessing,
    ],
  )

  return (
    <>
      <div
        className={classnames(
          'model-card model-card--inert',
          isMenuOpen && 'model-card--menu-open',
          isProcessing && 'model-card--processing',
        )}
      >
        {previewUrl && (
          <img
            className={classnames('model-card__image', isProcessing && 'model-card__image--processing')}
            src={previewUrl}
            alt={imageTitle}
          />
        )}
        {isProcessing && (
          <div className="model-card__processing">
            <Icon svgComponent={RotatingSpinner} size={spinnerSize} color="primary" notSpaced />
            <span className="model-card__processing-text">{t('sidebar.status.processing')}</span>
          </div>
        )}
        <Dropdown
          icon={icon}
          content={content}
          placement="bottom-start"
          className="model-card__dropdown-trigger"
          contentClassName="model-card__dropdown-content"
          triggerCloseDropdown={shouldCloseDropdown}
          setTriggerCloseDropdown={setShouldCloseDropdown}
          onOpenChange={setIsMenuOpen}
          portalZIndex={PORTAL_Z_INDEX}
          usePortal
        />
      </div>
      {isDeleteModalOpen && (
        <DeleteConfirmModal
          itemType="image"
          itemName={imageTitle}
          isSubmitDisabled={isDeleting}
          onSubmit={handleConfirmDelete}
          onCancel={handleCloseDelete}
        />
      )}
    </>
  )
}

ImageCard.propTypes = {
  runId: oneOfType([string, number]).isRequired,
  attributes: shape({
    outputAssets: arrayOf(shape({ previewUrl: string, fileUrl: string })),
    title: string,
    prompt: string,
    createdAt: string.isRequired,
    private: bool,
  }).isRequired,
  isProcessing: bool,
}

export default memo(ImageCard)
